import React from 'react';
import {
  AdminSettings,
  AircraftType,
  Booking,
  Customer,
  Quote,
  Role,
  UserAccount,
} from '../types';
import AgentAdvisories from './AgentAdvisories';
import PassengerManifestModal from './PlanningBoard/PassengerManifestModal';
import PlanningExecutionBoard from './PlanningBoard/PlanningExecutionBoard';
import PlanningManifestHub from './PlanningBoard/PlanningManifestHub';
import PlanningHistoryPanel from './PlanningBoard/PlanningHistoryPanel';
import PlanningToolbar from './PlanningBoard/PlanningToolbar';
import { usePlanningBoardData } from '../hooks/usePlanningBoardData';

interface PlanningBoardProps {
  quotes: Quote[];
  bookings: Booking[];
  currentRole: Role;
  customers: Customer[];
  aircrafts: AircraftType[];
  settings: AdminSettings;
  initialSelectedDate?: string;
  initialViewMode?: 'TACTICAL' | 'CREW' | 'GROUND' | 'HISTORY' | 'MANIFEST' | 'ROUTE';
  isDesktopWorkspace?: boolean;
  onOpenDesktopWorkspace?: (dateKey: string) => void;
  onOpenDesktopCrewWorkspace?: (dateKey: string) => void;
  onExitDesktopWorkspace?: (dateKey?: string) => void;
  onFinishDesktopWorkspace?: () => void;
  onUpdateQuote?: (quoteId: string, updates: Partial<Quote>) => void;
  onUpdateAircraft?: (aircraftId: string, updates: Partial<AircraftType>) => void;
  users: UserAccount[];
}

const PlanningBoard: React.FC<PlanningBoardProps> = ({
  quotes,
  bookings,
  currentRole,
  aircrafts,
  settings,
  initialSelectedDate,
  initialViewMode,
  isDesktopWorkspace = false,
  onOpenDesktopWorkspace,
  onOpenDesktopCrewWorkspace,
  onExitDesktopWorkspace,
  onFinishDesktopWorkspace,
  users,
  onUpdateQuote,
  onUpdateAircraft,
}) => {
  const planner = usePlanningBoardData({
    quotes,
    bookings,
    aircrafts,
    currentRole,
    settings,
    users,
    initialSelectedDate,
    initialViewMode,
    onUpdateQuote,
    onUpdateAircraft,
  });

  const plannerDateActivity = React.useMemo(() => {
    const map: Record<string, { charter: number; scheduled: number }> = {};
    const add = (dateKey: string, kind: 'charter' | 'scheduled', amount = 1) => {
      if (!dateKey) return;
      if (!map[dateKey]) map[dateKey] = { charter: 0, scheduled: 0 };
      map[dateKey][kind] += amount;
    };

    const normalizeDateKey = (value?: string | null): string => {
      if (!value) return '';
      if (value.length >= 10 && value[4] === '-' && value[7] === '-') return value.slice(0, 10);
      const parsed = new Date(value);
      if (Number.isNaN(parsed.getTime())) return '';
      const year = parsed.getFullYear();
      const month = String(parsed.getMonth() + 1).padStart(2, '0');
      const day = String(parsed.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };

    const resolveLegDateKey = (quoteDate: string, etd?: string | null): string => {
      const legDate = normalizeDateKey(etd || '');
      if (legDate) return legDate;
      return normalizeDateKey(quoteDate);
    };

    (Array.isArray(quotes) ? quotes : []).forEach((q) => {
      if (q.productMode !== 'CHARTER') return;
      if (q.status === 'DRAFT' || q.status === 'REJECTED') return;
      const legs = Array.isArray(q.legs) && q.legs.length > 0 ? q.legs : [];
      if (legs.length === 0) {
        add(normalizeDateKey(q.date), 'charter', 1);
        return;
      }
      legs.forEach((leg) => add(resolveLegDateKey(q.date, leg.etd), 'charter', 1));
    });
    (Array.isArray(bookings) ? bookings : []).forEach((b) => {
      add(normalizeDateKey(b.date), 'scheduled', 1);
    });
    Object.entries(planner.scheduledPlanExternalCountByDate || {}).forEach(([dateKey, countRaw]) => {
      const count = Number(countRaw || 0);
      if (count > 0) add(dateKey, 'scheduled', count);
    });
    return map;
  }, [quotes, bookings, planner.scheduledPlanExternalCountByDate]);

  return (
    <div className="max-w-full mx-auto pb-8 animate-in fade-in space-y-3 h-full flex flex-col">
      {isDesktopWorkspace && (
        <div className="rounded-3xl border border-slate-700/80 bg-[radial-gradient(circle_at_0%_0%,rgba(99,102,241,0.32),transparent_35%),radial-gradient(circle_at_100%_0%,rgba(16,185,129,0.24),transparent_32%),linear-gradient(140deg,#081226_0%,#0d1f3e_48%,#0b1d39_100%)] p-4 text-white shadow-[0_20px_55px_rgba(15,23,42,0.45)]">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <p className="text-[10px] font-black uppercase tracking-[0.22em] text-emerald-300">Planner Desktop</p>
              <p className="text-2xl font-black tracking-tight text-white">Operational Day {planner.selectedDate}</p>
              <div className="mt-2 flex flex-wrap items-center gap-2">
                <span className="rounded-full border border-white/20 bg-white/5 px-2.5 py-1 text-[10px] font-black uppercase tracking-[0.12em] text-slate-100">
                  Mode {planner.viewMode === 'TACTICAL' ? 'Aircraft Assign' : planner.viewMode === 'MANIFEST' ? 'Manifest Hub' : planner.viewMode}
                </span>
                <span className="rounded-full border border-indigo-300/40 bg-indigo-500/20 px-2.5 py-1 text-[10px] font-black uppercase tracking-[0.12em] text-indigo-100">
                  Charter Pending {planner.charterUnassignedCount + planner.charterCrewInvalidCount}
                </span>
                <span className="rounded-full border border-emerald-300/40 bg-emerald-500/15 px-2.5 py-1 text-[10px] font-black uppercase tracking-[0.12em] text-emerald-100">
                  Scheduled Pending {planner.scheduledUnassignedCount}
                </span>
                <span className={`rounded-full border px-2.5 py-1 text-[10px] font-black uppercase tracking-[0.12em] ${planner.charterConfirmed ? 'border-emerald-300/40 bg-emerald-500/25 text-emerald-100' : 'border-amber-300/40 bg-amber-500/20 text-amber-100'}`}>
                  Charter {planner.charterConfirmed ? 'Confirmed' : 'Pending'}
                </span>
                <span className={`rounded-full border px-2.5 py-1 text-[10px] font-black uppercase tracking-[0.12em] ${planner.scheduledConfirmed ? 'border-emerald-300/40 bg-emerald-500/25 text-emerald-100' : 'border-amber-300/40 bg-amber-500/20 text-amber-100'}`}>
                  Scheduled {planner.scheduledConfirmed ? 'Confirmed' : 'Pending'}
                </span>
                <span
                  title={
                    planner.scheduledPersistenceStatus === 'saved_to_server'
                      ? 'Scheduled tail/crew assignments are persisted to server.'
                      : planner.scheduledPersistenceStatus === 'saved_to_local_fallback'
                      ? 'Scheduled assignments are stored in local fallback cache only.'
                      : planner.scheduledPersistenceStatus === 'failed'
                      ? 'Scheduled assignment persistence failed.'
                      : 'Scheduled assignments pending sync.'
                  }
                  className={`rounded-full border px-2.5 py-1 text-[10px] font-black uppercase tracking-[0.12em] ${
                    planner.scheduledPersistenceStatus === 'saved_to_server'
                      ? 'border-emerald-300/40 bg-emerald-500/25 text-emerald-100'
                      : planner.scheduledPersistenceStatus === 'saved_to_local_fallback'
                      ? 'border-amber-300/40 bg-amber-500/20 text-amber-100'
                      : planner.scheduledPersistenceStatus === 'failed'
                      ? 'border-rose-300/40 bg-rose-500/20 text-rose-100'
                      : 'border-slate-300/30 bg-white/10 text-slate-100'
                  }`}
                >
                  Scheduled Store{' '}
                  {planner.scheduledPersistenceStatus === 'saved_to_server'
                    ? 'Server'
                    : planner.scheduledPersistenceStatus === 'saved_to_local_fallback'
                    ? 'Local Only'
                    : planner.scheduledPersistenceStatus === 'failed'
                    ? 'Failed'
                    : 'Pending'}
                </span>
              </div>
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <div className="rounded-xl border border-indigo-300/30 bg-indigo-500/15 px-3 py-1.5">
                  <p className="text-[10px] font-black uppercase tracking-[0.12em] text-indigo-100">1 Assign Aircraft</p>
                </div>
                <div className="rounded-xl border border-slate-300/30 bg-white/10 px-3 py-1.5">
                  <p className="text-[10px] font-black uppercase tracking-[0.12em] text-slate-100">2 Assign Crew</p>
                </div>
                <div className="rounded-xl border border-emerald-300/30 bg-emerald-500/15 px-3 py-1.5">
                  <p className="text-[10px] font-black uppercase tracking-[0.12em] text-emerald-100">3 Confirm Plan</p>
                </div>
              </div>
              <div className="mt-3 flex flex-wrap items-center gap-1 rounded-xl border border-white/10 bg-white/5 p-1">
                {(['TACTICAL', 'CREW', 'GROUND', 'ROUTE', 'MANIFEST', 'HISTORY'] as const).map((mode) => (
                  <button
                    key={mode}
                    type="button"
                    onClick={() => planner.setViewMode(mode)}
                    className={`rounded-lg px-2.5 py-1 text-[10px] font-black uppercase tracking-[0.12em] transition ${
                      planner.viewMode === mode
                        ? 'bg-indigo-500 text-white'
                        : 'text-slate-200 hover:bg-white/10'
                    }`}
                  >
                    {mode === 'TACTICAL'
                      ? 'Flight Planner'
                      : mode === 'CREW'
                      ? 'DFO Control'
                      : mode === 'GROUND'
                      ? 'Ground Ops'
                      : mode === 'ROUTE'
                      ? 'Route Preview'
                      : mode === 'MANIFEST'
                      ? 'Manifest Hub'
                      : 'History'}
                  </button>
                ))}
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={() => planner.setCharterConfirmed(true)}
                disabled={planner.charterUnassignedCount > 0 || planner.charterCrewInvalidCount > 0}
                title="Session-only toggle (does not persist to backend yet)."
                className={`rounded-xl px-3 py-2 text-[10px] font-black uppercase tracking-[0.12em] transition-all ${
                  planner.charterConfirmed
                    ? 'bg-emerald-500 text-white'
                    : planner.charterUnassignedCount > 0 || planner.charterCrewInvalidCount > 0
                    ? 'bg-white/10 text-slate-300 cursor-not-allowed border border-white/10'
                    : 'bg-indigo-500 text-white hover:bg-indigo-400'
                }`}
              >
                {planner.charterConfirmed ? 'Charter Confirmed' : `Confirm Charter (${planner.charterUnassignedCount + planner.charterCrewInvalidCount})`}
              </button>
              <button
                type="button"
                onClick={() => planner.setScheduledConfirmed(true)}
                disabled={planner.scheduledUnassignedCount > 0}
                title="Session-only toggle (does not persist to backend yet)."
                className={`rounded-xl px-3 py-2 text-[10px] font-black uppercase tracking-[0.12em] transition-all ${
                  planner.scheduledConfirmed
                    ? 'bg-emerald-500 text-white'
                    : planner.scheduledUnassignedCount > 0
                    ? 'bg-white/10 text-slate-300 cursor-not-allowed border border-white/10'
                    : 'bg-indigo-500 text-white hover:bg-indigo-400'
                }`}
              >
                {planner.scheduledConfirmed ? 'Scheduled Confirmed' : `Confirm Scheduled (${planner.scheduledUnassignedCount})`}
              </button>
              <button
                type="button"
                onClick={() => onExitDesktopWorkspace?.(planner.selectedDate)}
                className="rounded-xl border border-white/20 bg-white/5 px-3 py-2 text-[10px] font-black uppercase tracking-[0.12em] text-white hover:bg-white/10"
              >
                Back To Main Planner
              </button>
              <button
                type="button"
                onClick={() => {
                  planner.setViewMode('CREW');
                  onOpenDesktopCrewWorkspace?.(planner.selectedDate);
                }}
                className="rounded-xl border border-indigo-300/40 bg-indigo-500/20 px-3 py-2 text-[10px] font-black uppercase tracking-[0.12em] text-indigo-100 hover:bg-indigo-500/30"
              >
                Next: DFO Crew
              </button>
              <button
                type="button"
                onClick={onFinishDesktopWorkspace || onExitDesktopWorkspace}
                className="rounded-xl bg-emerald-500 px-3 py-2 text-[10px] font-black uppercase tracking-[0.12em] text-white hover:bg-emerald-400"
              >
                Finish Planning
              </button>
            </div>
          </div>
        </div>
      )}

      {settings.agent?.enabled && settings.agent.modules?.PLANNER && settings.agent.rolesByModule?.PLANNER?.includes(currentRole) && (
        <AgentAdvisories
          advisories={planner.advisories}
          mode={settings.agent.advisoryMode}
          moduleLabel="Dispatch"
          onAcknowledge={planner.acknowledgeAdvisories}
          showEmptyState
        />
      )}

      {!isDesktopWorkspace && (
        <PlanningToolbar
          opStatus={planner.opStatus}
          viewMode={planner.viewMode}
          selectedDate={planner.selectedDate}
          onSetViewMode={planner.setViewMode}
          onSetSelectedDate={planner.setSelectedDate}
          charterConfirmed={planner.charterConfirmed}
          scheduledConfirmed={planner.scheduledConfirmed}
          charterUnassignedCount={planner.charterUnassignedCount}
          charterCrewInvalidCount={planner.charterCrewInvalidCount}
          scheduledUnassignedCount={planner.scheduledUnassignedCount}
          onConfirmCharter={() => planner.setCharterConfirmed(true)}
          onConfirmScheduled={() => planner.setScheduledConfirmed(true)}
          todayPax={planner.opsSummary.today.pax}
          todaySectors={planner.opsSummary.today.sectors}
          todayFuelEstimate={planner.opsSummary.today.fuelEstimate}
          dateActivity={plannerDateActivity}
          isDesktopWorkspace={isDesktopWorkspace}
          onOpenDesktopWorkspace={onOpenDesktopWorkspace}
        />
      )}

      {isDesktopWorkspace && (
        planner.viewMode === 'HISTORY' ? (
          <PlanningHistoryPanel
            rows={planner.historyRows}
            onOpenManifest={(row) => void planner.loadManifestByQuoteId(row.id, row.customerName || 'Passenger List')}
          />
        ) : planner.viewMode === 'MANIFEST' ? (
          <PlanningManifestHub
            selectedDate={planner.selectedDate}
            missions={planner.missions}
            historyRows={planner.historyRows}
            onOpenLegManifest={(payload) => planner.openAggregatedManifest(payload.title, payload.passengers)}
            onOpenHistoryManifest={(row) => {
              const mission = planner.missions.find((m) => {
                if (row.source === 'SCHEDULED') {
                  return String(m.bookingId || '') === String(row.bookingId || '');
                }
                return String(m.quoteId || '') === String(row.id || '');
              });
              if (mission) {
                void planner.openPaxModal(mission);
                return;
              }
              void planner.loadManifestByQuoteId(row.id, row.customerName || 'Passenger List');
            }}
          />
        ) : (
          <PlanningExecutionBoard
            selectedDate={planner.selectedDate}
            viewMode={planner.viewMode}
            isDFO={planner.isDFO}
            isFlightPlanner={planner.isFlightPlanner}
            draggedMissionId={planner.draggedMissionId}
            draggedPilotId={planner.draggedPilotId}
            crewLoading={planner.crewLoading}
            dutyFilteredCrew={planner.dutyFilteredCrew}
            crewDutyMap={planner.crewDutyMap}
            charterReservoir={planner.charterReservoir}
            scheduledReservoir={planner.scheduledReservoir}
            fleetSchedule={planner.fleetSchedule}
            aircrafts={aircrafts}
            pilots={planner.pilots}
            crewLegErrors={planner.crewLegErrors}
            showFuelModal={planner.showFuelModal}
            fuelLiters={planner.fuelLiters}
            onSetShowFuelModal={planner.setShowFuelModal}
            onSetFuelLiters={planner.setFuelLiters}
            onDragMissionStart={planner.handleDragMissionStart}
            onDragPilotStart={planner.handleDragPilotStart}
            onDropOnReservoir={planner.handleDropOnReservoir}
            onDropOnAircraft={planner.handleDropOnAircraft}
            onAssignMissionToAircraft={planner.handleAssignMissionToAircraft}
            onDropPilotOnLeg={planner.handleDropPilotOnLeg}
            onOpenPaxModal={(mission) => void planner.openPaxModal(mission)}
            onUnassignPilot={planner.handleUnassignPilot}
            onUpdateQuote={onUpdateQuote}
            onUnassignScheduledLeg={(bookingId) => planner.setScheduledAssignments(prev => ({ ...prev, [bookingId]: 'UNASSIGNED' }))}
            onHandleFuelCommit={planner.handleFuelCommit}
            plannerRoutesByTail={planner.plannerRoutesByTail}
            routeSavingTail={planner.routeSavingTail}
            onSaveGeneratedRouteForTail={planner.handleSaveGeneratedRouteForTail}
            onSaveManualRouteEdits={planner.handleSaveManualRouteEdits}
            onLockRoute={planner.handleLockRoute}
            onConfirmRoute={planner.handleConfirmRoute}
            onResetRoute={planner.handleResetRoute}
            getCrewRoleCode={planner.getCrewRoleCode}
          />
        )
      )}

      {planner.assignmentToast && (
        <div className="pointer-events-none fixed bottom-6 right-6 z-[160] animate-in slide-in-from-bottom-3 fade-in duration-200">
          <div className={`min-w-[260px] rounded-2xl border px-4 py-3 shadow-[0_18px_45px_rgba(15,23,42,0.35)] backdrop-blur ${
            planner.assignmentToast.startsWith('Moved')
              ? 'border-indigo-300/40 bg-[linear-gradient(140deg,rgba(30,64,175,0.92),rgba(67,56,202,0.92))] text-indigo-50'
              : 'border-emerald-300/40 bg-[linear-gradient(140deg,rgba(5,150,105,0.92),rgba(6,182,212,0.9))] text-white'
          }`}>
            <div className="flex items-center gap-2">
              <span className="inline-flex h-6 w-6 items-center justify-center rounded-full border border-white/30 bg-white/15">
                <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.4} d="M5 13l4 4L19 7" />
                </svg>
              </span>
              <p className="text-[9px] font-black uppercase tracking-[0.2em]">
                {planner.assignmentToast.startsWith('Moved') ? 'Queue Updated' : 'Assignment Saved'}
              </p>
            </div>
            <p className="mt-1 text-[12px] font-black uppercase tracking-[0.12em]">{planner.assignmentToast}</p>
          </div>
        </div>
      )}

      <PassengerManifestModal
        open={planner.paxModalOpen}
        title={planner.paxModalTitle}
        issuedAt={planner.paxModalIssuedAt}
        issuedBy={planner.paxModalIssuedBy}
        passengers={planner.paxModalPassengers}
        loading={planner.paxModalLoading}
        error={planner.paxModalError}
        issueLoading={planner.paxIssueLoading}
        canIssue={!!planner.paxModalQuoteId && planner.paxModalPassengers.length > 0 && !planner.paxModalIssuedAt}
        canDownloadHtml={planner.canDownloadManifestHtml}
        canPrint={planner.canPrintManifest}
        canEmail={planner.canEmailManifest}
        emailTo={planner.paxEmailTo}
        emailCc={planner.paxEmailCc}
        emailBcc={planner.paxEmailBcc}
        emailSending={planner.paxEmailSending}
        onEmailToChange={planner.setPaxEmailTo}
        onEmailCcChange={planner.setPaxEmailCc}
        onEmailBccChange={planner.setPaxEmailBcc}
        onClose={() => planner.setPaxModalOpen(false)}
        onIssue={planner.handleIssueManifest}
        onDownloadCsv={planner.handleDownloadManifestCsv}
        onDownloadHtml={planner.handleDownloadManifestHtml}
        onPrint={planner.handlePrintManifest}
        onEmail={planner.handleEmailManifest}
      />
    </div>
  );
};

export default PlanningBoard;
